import { useMemo } from 'react'
import type { RepartoMes } from '../../hooks/useCalendarioMes'
import { COLORES_DIA_LISTA } from '../agendamiento/coloresDia'
import { marcaDeEstado } from '../agendamiento/estadoReparto'
import { Modal } from '../../componentes/Modal'
import { IconoCerrar } from '../../componentes/iconos'
import { BarraPaginacion } from '../../componentes/BarraPaginacion'
import { usePaginacionLocal } from '../../componentes/paginacion'

/**
 * Modal del DÍA seleccionado en el calendario mensual (opción "ver + añadir").
 *
 * Al pulsar una celda del mes se abre este modal (no un panel lateral que crece
 * y mete scroll de página): los repartos van AGRUPADOS POR RUTA y el contenido
 * scrollea dentro del modal. Botón para añadir uno nuevo. Es presentacional:
 * recibe la lista y los callbacks; la página decide el alta de reparto (B.3).
 */

interface Props {
  /** Fecha ISO del día abierto (YYYY-MM-DD). */
  iso: string
  /** Repartos de ese día. */
  repartos: RepartoMes[]
  /** Añadir un reparto nuevo en este día. */
  onAnadir: (iso: string) => void
  /** Cerrar el modal. */
  onCerrar: () => void
}

/** Fecha ISO → texto legible "lunes, 15 de septiembre de 2026". */
function fechaLegible(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const fecha = new Date(y, m - 1, d)
  return fecha.toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/** Nombre legible de una ruta a partir de su clave (r1..r5). */
const NOMBRE_RUTA: Record<string, string> = {
  r1: 'R1 · Lunes',
  r2: 'R2 · Martes',
  r3: 'R3 · Miércoles',
  r4: 'R4 · Jueves',
  r5: 'R5 · Viernes',
}

function FilaReparto({ reparto }: { reparto: RepartoMes }) {
  const color = reparto.ruta
    ? COLORES_DIA_LISTA.find((c) => c.clave === reparto.ruta)
    : undefined
  const marca = marcaDeEstado(reparto.estado)
  return (
    <li className="flex items-center gap-2 rounded-md border border-borde-suave bg-lienzo px-2.5 py-2">
      <span
        aria-hidden
        className={['inline-block h-2.5 w-2.5 shrink-0 rounded-sm ring-1', color?.fondo ?? 'bg-superficie', color?.anillo ?? 'ring-borde'].join(' ')}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-tinta">{reparto.cliente}</p>
        <p className="flex flex-wrap gap-x-2 text-xs text-tinta-apagada">
          {reparto.bono ? <span>{reparto.bono}</span> : null}
          {reparto.ruta ? <span className="uppercase">{reparto.ruta}</span> : null}
        </p>
      </div>
      <span className="flex shrink-0 items-center gap-1 text-xs text-tinta-2">
        <marca.Icono aria-hidden="true" className="size-3.5 shrink-0" />
        {marca.etiqueta}
      </span>
    </li>
  )
}

/** Agrupa los repartos por ruta, conservando el orden r1..r5 y "sin ruta" al final. */
function agruparPorRuta(repartos: RepartoMes[]): { ruta: string; nombre: string; items: RepartoMes[] }[] {
  const grupos = new Map<string, RepartoMes[]>()
  for (const r of repartos) {
    const clave = r.ruta ?? 'sin'
    const lista = grupos.get(clave) ?? []
    lista.push(r)
    grupos.set(clave, lista)
  }
  const orden = ['r1', 'r2', 'r3', 'r4', 'r5', 'sin']
  return orden
    .filter((clave) => grupos.has(clave))
    .map((clave) => ({
      ruta: clave,
      nombre: NOMBRE_RUTA[clave] ?? 'Sin ruta',
      items: grupos.get(clave)!,
    }))
}

/** Los repartos de una ruta en el día, de 15 en 15. */
function GrupoDia({ nombre, items }: { nombre: string; items: RepartoMes[] }) {
  const pag = usePaginacionLocal(items)
  return (
    <section aria-label={nombre} className="flex flex-col gap-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-tinta-apagada">
        {nombre} <span className="text-tinta-2">({items.length})</span>
      </h4>
      <ul className="flex flex-col gap-1.5">
        {pag.visibles.map((r) => (
          <FilaReparto key={r.id} reparto={r} />
        ))}
      </ul>
      <BarraPaginacion pagina={pag.pagina} paginas={pag.paginas} onCambiar={pag.irAPagina} etiqueta={`Paginación de ${nombre}`} />
    </section>
  )
}

export function PanelDia({ iso, repartos, onAnadir, onCerrar }: Props) {
  const carga = repartos.length
  const grupos = useMemo(() => agruparPorRuta(repartos), [repartos])

  return (
    <Modal titulo={`Repartos del ${fechaLegible(iso)}`} onCerrar={onCerrar} anchoMax="max-w-3xl">
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-3 border-b border-borde px-5 py-4">
        <div>
          <h3 className="text-base font-semibold capitalize text-tinta">{fechaLegible(iso)}</h3>
          <p className="mt-0.5 flex items-center gap-2 text-xs text-tinta-apagada">
            {carga > 0 ? (
              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-acento px-1.5 py-0.5 text-xs font-bold text-acento-contraste">
                {carga}
              </span>
            ) : null}
            {carga === 0 ? 'Sin repartos' : carga === 1 ? 'reparto' : 'repartos'}
          </p>
        </div>
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar el día"
          className="grid size-8 place-items-center rounded-md text-tinta-2 ring-1 ring-borde hover:bg-lienzo focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco"
        >
          <IconoCerrar aria-hidden="true" className="size-4" />
        </button>
      </div>

      {/* Cuerpo: scrollea DENTRO del modal (la página no se mueve) */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {carga > 0 ? (
          <div className="grid gap-5 sm:grid-cols-2">
            {grupos.map((grupo) => (
              <GrupoDia key={grupo.ruta} nombre={grupo.nombre} items={grupo.items} />
            ))}
          </div>
        ) : (
          <p className="rounded-md bg-lienzo px-3 py-6 text-center text-sm text-tinta-apagada">
            No hay repartos agendados este día.
          </p>
        )}
      </div>

      {/* Pie fijo con la acción */}
      <div className="border-t border-borde px-5 py-4">
        <button
          type="button"
          onClick={() => onAnadir(iso)}
          className="w-full rounded-lg bg-acento px-3 py-2.5 text-sm font-medium text-acento-contraste hover:brightness-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco"
        >
          + Añadir reparto
        </button>
      </div>
    </Modal>
  )
}
